import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Banner, EmptyState, Spinner } from '../components/States';
import { useAuth } from '../hooks/useAuth';
import { dateTime } from '../lib/format';
import type { Order } from '../api/types';
import { ETAPAS, NOME_DO_TIPO, brl, posicaoDaEtapa, textoParaCliente, valorDoPedido } from '../lib/pedidos';

/**
 * Histórico do cliente.
 *
 * Cada pedido mostra a etapa com o mesmo nome da tela do pedido e uma barra de
 * três partes: dá para ver de relance o que ainda anda e o que já terminou.
 */
export function OrdersPage() {
  const { user, loading: carregandoConta, loginUrl } = useAuth();
  const [items, setItems] = useState<Order[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void api
      .meusPedidos()
      .then((r) => setItems(r.items))
      .catch((e) => setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os pedidos.'));
  }, [user]);

  if (carregandoConta) return <Spinner label="Carregando…" />;
  if (!user) {
    return (
      <div className="panel narrow">
        <h2>Meus pedidos</h2>
        <p className="muted">Entre com a Steam para ver os seus pedidos.</p>
        <a className="btn primary" href={loginUrl}>
          Entrar com a Steam
        </a>
      </div>
    );
  }

  if (erro) return <Banner kind="error">{erro}</Banner>;
  if (!items) return <Spinner label="Carregando os seus pedidos…" />;
  if (items.length === 0) {
    return (
      <EmptyState>
        {/* Link, e não <a href>: âncora comum recarrega o site inteiro. */}
        Você ainda não fez nenhum pedido. <Link to="/all">Ver o catálogo</Link>
      </EmptyState>
    );
  }

  const naoEnviados = items.filter((o) => o.status === 'aberto');

  return (
    <div className="panel">
      <h2>Meus pedidos</h2>

      {naoEnviados.length > 0 && (
        <Banner kind="warn">
          {naoEnviados.length === 1
            ? 'Você tem 1 pedido que ainda não chegou ao atendimento. Abra para enviar.'
            : `Você tem ${naoEnviados.length} pedidos que ainda não chegaram ao atendimento. Abra para enviar.`}
        </Banner>
      )}

      <ul className="meus-pedidos">
        {items.map((o) => {
          const texto = textoParaCliente(o.kind, o.status);
          const posicao = posicaoDaEtapa(o.status);
          const capa = o.items.find((i) => i.side === 'saida') ?? o.items[0];
          return (
            <li key={o.code}>
              <Link to={`/pedido/${o.code}`} className={`meu-pedido ${o.status}`}>
                {capa?.iconUrl ? <img src={capa.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                <div className="meu-pedido-info">
                  <span className="muted">
                    {o.code} · {NOME_DO_TIPO[o.kind]} · {dateTime(o.createdAt)}
                  </span>
                  <b>{texto.titulo}</b>
                  <span className="muted">
                    {o.items.length} {o.items.length === 1 ? 'item' : 'itens'}
                    {capa ? ` · ${capa.name}` : ''}
                  </span>
                  {o.status !== 'cancelado' && (
                    <span className="progresso" aria-label={`Etapa ${posicao} de ${ETAPAS.length}`}>
                      {ETAPAS.map((s, i) => (
                        <i key={s} className={i < posicao ? 'cheia' : ''} />
                      ))}
                    </span>
                  )}
                </div>
                <b className="meu-pedido-valor">{brl(valorDoPedido(o))}</b>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
